import React from 'react';
import styles from './InputField.module.scss';
import { TextField, MenuItem, Divider } from '@mui/material';
import classNames from 'classnames';

interface DropDownFieldProps<
  T extends string | number | readonly string[] | undefined,
> {
  title?: string;
  inputName: string;
  placeholder?: string;
  value: T;
  options?: Array<{ value: T; label: string }>;
  icon?: React.ReactNode;
  onChange: (value: string) => void;
  onReselect?: (value: string) => void;
  error?: boolean;
  className?: string;
  autoComplete?: string;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  type?: 'number' | 'time' | 'dropdown';
  validationMessage?: string;
  renderValue?: (value: T) => React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const formatTimeValue = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatDropDownValue = (value: any) => {
  if (value === 'NaN') {
    return '';
  }
  return value;
};

const parseTimeValue = (timeString: string) => {
  if (timeString.includes(':')) {
    const [minutes, seconds] = timeString.split(':').map(Number);
    return minutes * 60 + seconds;
  }
  return Number(timeString);
};

const sanitizeNumericValue = (input: string) => {
  if (input.length > 1 && input.startsWith('0')) {
    return input.replace(/^0+/, '');
  }
  return input;
};

const InputField = <T extends string | number | readonly string[] | undefined>({
  options,
  value,
  icon,
  type,
  onChange,
  onReselect,
  inputName,
  autoComplete,
  title,
  placeholder,
  onBlur,
  error,
  className,
  validationMessage,
  renderValue,
  inputProps,
  onKeyDown,
}: DropDownFieldProps<T>) => {
  const isSelect = !!options?.length;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (type === 'time') {
      onChange(String(parseTimeValue(newValue)));
    } else {
      onChange(sanitizeNumericValue(newValue));
    }
  };

  const displayedValue =
    type === 'time'
      ? formatTimeValue(Number(value))
      : type === 'dropdown'
        ? formatDropDownValue(value)
        : value;

  return (
    <div className={classNames(styles.inputFieldItem, className)}>
      {(icon || title) && (
        <div className={styles.fieldTitleWrapper}>
          {icon}
          {title && <span className={styles.fieldTitle}>{title}</span>}
        </div>
      )}
      <div className={styles.inputWrapper}>
        <TextField
          name={inputName}
          margin="dense"
          select={isSelect}
          fullWidth
          type={type === 'time' ? 'text' : type}
          autoComplete={autoComplete}
          variant="outlined"
          value={displayedValue}
          onChange={handleChange}
          error={error}
          className={classNames(styles.inputField, {
            [styles.inputFieldDropDown]: isSelect,
            [styles.inputFieldError]: error,
          })}
          onBlur={onBlur}
          onKeyDown={onKeyDown as any}
          inputProps={inputProps}
          SelectProps={
            isSelect
              ? {
                displayEmpty: true,
                ...(renderValue && { renderValue: (v) => renderValue(v as T) }),
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    className: styles.dropdownMenuPaper,
                    style: {
                      maxHeight: 250,
                    },
                  },
                  disableScrollLock: true,
                },
              }
              : undefined
          }
        >
          {options?.length &&
            options.map((option, index) => [
              <MenuItem
                key={index}
                value={option.value}
                className={styles.dropdownMenuItem}
                onClick={
                  onReselect && option.value === value
                    ? () => onReselect(String(option.value))
                    : undefined
                }
              >
                {option.label}
              </MenuItem>,
              index < options.length - 1 && (
                <Divider key={`divider-${index}`} className={styles.divider} />
              ),
            ])}
        </TextField>
        <span
          className={classNames(styles.inputFieldPlaceholder, {
            [styles.inputFieldPlaceholderHidden]: !!value,
            [styles.dropDownInputPlaceholder]:
              type === 'dropdown' && value === 'NaN',
          })}
        >
          {placeholder}
        </span>
      </div>
      {error && validationMessage && (
        <span className={styles.validationMessage}>{validationMessage}</span>
      )}
    </div>
  );
};

export default InputField;